import { PromiseResolver } from "@yume-chan/async";
import { decodeUtf8 } from "../utils/index.js";
import { BufferedStream, BufferedStreamEndedError } from "./buffered.js";
import { AbortController, ReadableStream, TransformStream, WritableStream } from "./detect.js";
export class DuplexStreamFactory {
    readableControllers = [];
    pushReadableControllers = [];
    _closed = new PromiseResolver();
    get closed() { return this._closed.promise; }
    options;
    _closeRequestedByReadable = false;
    _writableClosed = false;
    constructor(options) {
        this.options = options ?? {};
    }
    createPushReadable(source, strategy) {
        return new PushReadableStream(controller => {
            this.pushReadableControllers.push(controller);
            controller.abortSignal.addEventListener('abort', async () => {
                this._closeRequestedByReadable = true;
                await this.close();
            });
            source({
                abortSignal: controller.abortSignal,
                async enqueue(chunk) {
                    await controller.enqueue(chunk);
                },
                close: async () => {
                    controller.close();
                    this._closeRequestedByReadable = true;
                    await this.close();
                },
                error: async (e) => {
                    controller.error(e);
                    this._closeRequestedByReadable = true;
                    await this.close();
                },
            });
        }, strategy);
    }
    ;
    createWrapReadable(readable) {
        return new WrapReadableStream({
            async start() {
                return {
                    readable,
                    state: undefined,
                };
            },
            close: async () => {
                this._closeRequestedByReadable = true;
                await this.close();
            },
        });
    }
    createReadable(source, strategy) {
        return new ReadableStream({
            start: async (controller) => {
                this.readableControllers.push(controller);
                await source?.start?.(controller);
            },
            pull: (controller) => {
                return source?.pull?.(controller);
            },
            cancel: async (reason) => {
                await source?.cancel?.(reason);
                this._closeRequestedByReadable = true;
                await this.close();
            },
        }, strategy);
    }
    createWritable(sink, strategy) {
        return new WritableStream({
            start: async (controller) => {
                await sink.start?.(controller);
            },
            write: async (chunk, controller) => {
                if (this._writableClosed) {
                    throw new Error("stream is closed");
                }
                await sink.write?.(chunk, controller);
            },
            close: async () => {
                await sink.close?.();
                this.close();
            },
            abort: async (reason) => {
                await sink.abort?.(reason);
                await this.close();
            },
        }, strategy);
    }
    async closeReadableStreams() {
        this._closed.resolve();
        await this.options.close?.();
        for (const controller of this.readableControllers) {
            try {
                controller.close();
            }
            catch { }
        }
        for (const controller of this.pushReadableControllers) {
            try {
                controller.close();
            }
            catch { }
        }
    }
    async close() {
        this._writableClosed = true;
        if (this._closeRequestedByReadable ||
            !this.options.preventCloseReadableStreams) {
            await this.closeReadableStreams();
        }
    }
}
export class DecodeUtf8Stream extends TransformStream {
    constructor() {
        super({
            transform(chunk, controller) {
                controller.enqueue(decodeUtf8(chunk));
            },
        });
    }
}
export class GatherStringStream extends TransformStream {
    // Optimization: rope (concat strings) is faster than `[].join('')`
    _result = '';
    get result() { return this._result; }
    constructor() {
        super({
            transform: (chunk) => {
                this._result += chunk;
            },
        });
    }
}
// TODO: Find other ways to implement `StructTransformStream`
export class StructDeserializeStream {
    _readable;
    get readable() { return this._readable; }
    _writable;
    get writable() { return this._writable; }
    constructor(struct) {
        // Convert incoming chunks to a `BufferedStream`
        let incomingStreamController;
        const incomingStream = new BufferedStream(new PushReadableStream(controller => incomingStreamController = controller));
        this._readable = new PushReadableStream(async (controller) => {
            try {
                // Unless we make `deserialize` be capable of pausing/resuming,
                // We always need at least one pull loop
                while (true) {
                    const value = await struct.deserialize(incomingStream);
                    await controller.enqueue(value);
                }
            }
            catch (e) {
                if (e instanceof BufferedStreamEndedError) {
                    controller.close();
                    return;
                }
                throw e;
            }
        });
        this._writable = new WritableStream({
            async write(chunk) {
                await incomingStreamController.enqueue(chunk);
            },
            close() {
                // @ts-ignore
                console.log('deserialization stream closed');
                incomingStreamController.close();
            },
        });
    }
}
export class StructSerializeStream extends TransformStream {
    constructor(struct) {
        super({
            transform(chunk, controller) {
                controller.enqueue(struct.serialize(chunk));
            },
        });
    }
}
export class WrapWritableStream extends WritableStream {
    writable;
    writer;
    state;
    constructor(wrapper) {
        super({
            start: async () => {
                const { writable, state } = await wrapper.start();
                this.writable = writable;
                this.writer = writable.getWriter();
                this.state = state;
            },
            write: async (chunk) => {
                // Maintain back pressure
                await this.writer.ready;
                await this.writer.write(chunk);
            },
            abort: async (reason) => {
                await this.writer.abort(reason);
                wrapper.close?.(this.state);
            },
            close: async () => {
                await this.writer.close();
                await wrapper.close?.(this.state);
            },
        });
    }
}
export class WrapReadableStream extends ReadableStream {
    readable;
    reader;
    state;
    constructor(wrapper) {
        super({
            start: async () => {
                const { readable, state } = await wrapper.start();
                this.readable = readable;
                this.reader = readable.getReader();
                this.state = state;
            },
            cancel: async (reason) => {
                await this.reader.cancel(reason);
                wrapper.close?.(this.state);
            },
            pull: async (controller) => {
                const result = await this.reader.read();
                if (result.done) {
                    wrapper.close?.(this.state);
                    controller.close();
                }
                else {
                    controller.enqueue(result.value);
                }
            }
        });
    }
}
export class ChunkStream extends TransformStream {
    constructor(size) {
        super({
            transform(chunk, controller) {
                for (let start = 0; start < chunk.byteLength;) {
                    const end = start + size;
                    controller.enqueue(chunk.subarray(start, end));
                    start = end;
                }
            }
        });
    }
}
function* splitLines(text) {
    let start = 0;
    while (true) {
        const index = text.indexOf('\n', start);
        if (index === -1) {
            return;
        }
        const line = text.substring(start, index);
        yield line;
        start = index + 1;
    }
}
export class SplitLineStream extends TransformStream {
    constructor() {
        super({
            transform(chunk, controller) {
                for (const line of splitLines(chunk)) {
                    controller.enqueue(line);
                }
            }
        });
    }
}
/**
 * Create a new `WritableStream` that, when written to, will write that chunk to
 * `pair.writable`, when pipe `pair.readable` to `writable`.
 *
 * It's the opposite of `ReadableStream.pipeThrough`.
 *
 * @param writable The `WritableStream` to write to.
 * @param pair A `TransformStream` that converts chunks.
 * @returns A new `WritableStream`.
 */
export function pipeFrom(writable, pair) {
    const writer = pair.writable.getWriter();
    const pipe = pair.readable
        .pipeTo(writable);
    return new WritableStream({
        async write(chunk) {
            await writer.ready;
            await writer.write(chunk);
        },
        async close() {
            await writer.close();
            await pipe;
        }
    });
}
export class InspectStream extends TransformStream {
    constructor(callback) {
        super({
            transform(chunk, controller) {
                callback(chunk);
                controller.enqueue(chunk);
            }
        });
    }
}
export class PushReadableStream extends ReadableStream {
    constructor(source, strategy) {
        let waterMarkLow;
        const canceled = new AbortController();
        super({
            start: (controller) => {
                source({
                    abortSignal: canceled.signal,
                    async enqueue(chunk) {
                        // Only when the stream in errored, `desiredSize` will be `null`.
                        // But since `null <= 0` is `true`
                        // (`null <= 0` is evaluated as `!(null > 0)` => `!false` => `true`),
                        // not handling it will cause a deadlock.
                        if ((controller.desiredSize ?? 1) <= 0) {
                            waterMarkLow = new PromiseResolver();
                            await waterMarkLow.promise;
                        }
                        // `controller.enqueue` will throw error for us
                        // if the stream is already errored.
                        controller.enqueue(chunk);
                    },
                    close() {
                        controller.close();
                    },
                    error(e) {
                        controller.error(e);
                    },
                });
            },
            pull: () => {
                waterMarkLow?.resolve();
            },
            cancel: async (reason) => {
                waterMarkLow?.reject(reason);
                canceled.abort();
            },
        }, strategy);
    }
}
//# sourceMappingURL=transform.js.map